import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';

import { BankConnectFlowService } from 'src/shared/services/bank-connect-flow.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { FinancialConnectionsService, FinancialConnectionsAccount } from 'src/shared/services/financial-connections.service';

interface ConnectionView {
  account: FinancialConnectionsAccount;
  label: string;
  statusLabel: string;
  isActive: boolean;
}

@Component({
  templateUrl: 'manage-connections.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ManageConnectionsPage implements OnInit {

  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private demoBlock = inject(DemoBlockService);
  private bankConnectFlow = inject(BankConnectFlowService);
  private financialConnectionsService = inject(FinancialConnectionsService);

  connections: ConnectionView[] = [];
  loaded = false;
  connecting = false;
  isRightSideContent = true;
  dialogParams: any;

  boundAddAccount = () => this.addAccount();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.rightSidePanelService.setDialogPageTitle('Connected Bank Accounts');
    await this.loadPage();
  }

  ionViewWillEnter() {
    if (this.loaded) {
      this.loadPage();
    }
  }

  async loadPage() {
    try {
      const accounts = await this.financialConnectionsService.getConnectedAccounts();
      this.connections = accounts.map(account => this.toView(account));
    } catch {
      this.connections = [];
    }
    this.loaded = true;
  }

  // `status` is the link state Stripe reports: active means linked and refreshed daily;
  // it never flips while a sync is running, so there is no "syncing right now" signal.
  private toView(account: FinancialConnectionsAccount): ConnectionView {
    return {
      account,
      label: `${account.institution_name} ····${account.last4}`,
      statusLabel: account.status === 'active' ? 'Connected' : account.status === 'inactive' ? 'Removed' : 'Disconnected',
      isActive: account.status === 'active',
    };
  }

  async addAccount(): Promise<void> {
    if (this.demoBlock.block()) { return; }
    if (this.connecting) { return; }
    this.connecting = true;
    try {
      const result = await this.bankConnectFlow.connectBank();
      if (!result.linked) { return; }
      await this.storage.save('didMakeConnectionChanges', true);
      await this.loadPage();
      if (result.linkedConnections.length > 0) {
        this.rightSidePanelService.navigateTo('connection-history', { connections: result.linkedConnections, connection: null, isNewConnection: true });
      } else {
        // The webhook skips accounts TIDY already has on file, including removed ones,
        // so a re-linked account never becomes a new connection.
        this.util.showError('Stripe linked the account, but TIDY already has it on file. If it was removed earlier, re-adding it is not supported yet.', 8000);
      }
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to connect bank account.');
    } finally {
      this.connecting = false;
    }
  }

  viewTransactions(view: ConnectionView): void {
    this.rightSidePanelService.navigateTo('bank-transactions', { connectionId: view.account.id, connection: view.account });
  }

  changeStartDate(view: ConnectionView): void {
    if (this.demoBlock.block()) { return; }
    this.rightSidePanelService.navigateTo('connection-history', { connections: null, connection: view.account, isNewConnection: false });
  }

  removeAccount(view: ConnectionView): void {
    if (this.demoBlock.block()) { return; }
    this.rightSidePanelService.navigateTo('remove-connection', { connection: view.account });
  }
}
